import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const COLLAPSED_KEY = 'atlas-sidebar-collapsed';

interface SidebarContextType {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  sidebarWidth: number;
  mobileTitleOverride: string | null;
  setMobileTitleOverride: (title: string | null) => void;
  mobileBackOverride: (() => void) | null;
  setMobileBackOverride: (fn: (() => void) | null) => void;
  mobileHideBack: boolean;
  setMobileHideBack: (hide: boolean) => void;
  /** Si true, el header mobile pierde su `bg-white` + border y los
   *  botones (back / menu) toman fondo blanco circular semi-opaco.
   *  Pensado para pantallas como el preview de plantillas donde la
   *  imagen ocupa todo y el header debe flotar encima. */
  mobileHeaderTransparent: boolean;
  setMobileHeaderTransparent: (v: boolean) => void;
  /** Reemplaza el título centrado del header mobile por un ReactNode
   *  arbitrario (ej. un toggle, chips). Si es null, se muestra el
   *  título habitual. */
  mobileHeaderCenter: ReactNode | null;
  setMobileHeaderCenter: (node: ReactNode | null) => void;
  /** Acción opcional a la derecha del header mobile (ej. un botón "Listo").
   *  Se renderiza DENTRO del header (z-50, contexto raíz) para escapar el
   *  stacking context del shell de contenido (z-30 + transform-gpu); un botón
   *  `fixed` puesto desde la página quedaría tapado por el header transparente.
   *  Si es null, no se muestra nada a la derecha. */
  mobileRightAction: ReactNode | null;
  setMobileRightAction: (node: ReactNode | null) => void;
  /** Oculta el pill de créditos en el header mobile transparente.
   *  Útil cuando la pantalla ya muestra otros CTAs o cuando el
   *  contador no aporta valor (ej. resultados de catálogo). */
  mobileHideCreditsPill: boolean;
  setMobileHideCreditsPill: (v: boolean) => void;
  /** Oculta la barra de navegación inferior mobile. Útil cuando una
   *  pantalla-tab entra en "modo tarea" y muestra su propia barra de acción
   *  fija abajo (ej. selección de productos con el CTA de generar). */
  mobileHideBottomNav: boolean;
  setMobileHideBottomNav: (v: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  toggleCollapsed: () => {},
  sidebarWidth: 240,
  mobileTitleOverride: null,
  setMobileTitleOverride: () => {},
  mobileBackOverride: null,
  setMobileBackOverride: () => {},
  mobileHideBack: false,
  setMobileHideBack: () => {},
  mobileHeaderTransparent: false,
  setMobileHeaderTransparent: () => {},
  mobileHeaderCenter: null,
  setMobileHeaderCenter: () => {},
  mobileRightAction: null,
  setMobileRightAction: () => {},
  mobileHideCreditsPill: false,
  setMobileHideCreditsPill: () => {},
  mobileHideBottomNav: false,
  setMobileHideBottomNav: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === 'true'; } catch { return false; }
  });

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSED_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  // Listen for sidebar state changes via custom events (from Sidebar component)
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      setIsCollapsed(e.detail.isCollapsed);
      try { localStorage.setItem(COLLAPSED_KEY, String(e.detail.isCollapsed)); } catch {}
    };
    window.addEventListener('sidebar-toggle' as any, handler as EventListener);
    return () => window.removeEventListener('sidebar-toggle' as any, handler as EventListener);
  }, []);

  const [mobileTitleOverride, setMobileTitleOverride] = useState<string | null>(null);
  const [mobileBackOverride, setMobileBackOverride] = useState<(() => void) | null>(null);
  const [mobileHideBack, setMobileHideBack] = useState(false);
  const [mobileHeaderTransparent, setMobileHeaderTransparent] = useState(false);
  const [mobileHeaderCenter, setMobileHeaderCenter] = useState<ReactNode | null>(null);
  const [mobileRightAction, setMobileRightAction] = useState<ReactNode | null>(null);
  const [mobileHideCreditsPill, setMobileHideCreditsPill] = useState(false);
  const [mobileHideBottomNav, setMobileHideBottomNav] = useState(false);

  // Ancho de la sidebar (al ras, sin gap flotante). El contenido arranca
  // pegado a este borde y se monta "encima" con un panel de esquina curva.
  const sidebarWidth = isCollapsed ? 72 : 264;

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleCollapsed, sidebarWidth, mobileTitleOverride, setMobileTitleOverride, mobileBackOverride, setMobileBackOverride, mobileHideBack, setMobileHideBack, mobileHeaderTransparent, setMobileHeaderTransparent, mobileHeaderCenter, setMobileHeaderCenter, mobileRightAction, setMobileRightAction, mobileHideCreditsPill, setMobileHideCreditsPill, mobileHideBottomNav, setMobileHideBottomNav }}>
      {children}
    </SidebarContext.Provider>
  );
};
